import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef,
} from "react";
import { toast } from "../../../../utils/demoToast";
import { useTranslation } from "react-i18next";
import { getListOptions } from "../../../../services/listService";
import {
  getRestaurantDishCategories,
  addRestaurantDishCategory,
} from "../../../../services/restaurantService";
import { getVariantGroupsByRestaurant } from "../../../../services/dishVariantGroupService";
import Form from "../../../inputs/Form";
import { formatSwissPrice } from "../../../../utils/formatting";
import useApi from "../../../../hooks/useApi";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import Button from "../../../buttons/Button";
import Modal from "../../../utils/Modal";
import CategoryManager from "./CategoryManager";
import VariantGroupManager from "./VariantGroupManager";
import useModal from "../../../../hooks/useModal";
import UserAvatar from "../../../utils/UserAvatar";

const DishForm = forwardRef(function DishForm(
  {
    formData = null,
    dishId = null,
    lockedTypeId,
    onChange = () => {},
    setIsValid,
    fieldClass = "bg-white",
    tKey = "dishView.",
    imgPreview = false,
  },
  ref,
) {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [dropDownOptions, setDropDownOptions] = useState({});
  const {
    isModalOpen: isCategoryModalOpen,
    openModal: openCategoryModal,
    closeModal: closeCategoryModal,
  } = useModal();
  const {
    isModalOpen: isVariantModalOpen,
    openModal: openVariantModal,
    closeModal: closeVariantModal,
  } = useModal();
  const hasUpdatedType = useRef(false);
  const { request: getRestaurantDishCategoriesRequest } = useApi(
    getRestaurantDishCategories
  );
  const { request: addRestaurantDishCategoryRequest } = useApi(
    addRestaurantDishCategory
  );
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const lastDishTypeRef = useRef(formData?.dishtype?.value?.dishTypeId);

  useEffect(() => {
    const fetchListData = async () => {
      try {
        const [categoriesList, typesList, allergiesList, variantGroupsList] =
          await Promise.all([
            getRestaurantDishCategoriesRequest(restaurantId),
            getListOptions("dishtype", "casual/"),
            getListOptions("allergy"),
            getVariantGroupsByRestaurant(restaurantId),
          ]);
        setDropDownOptions({
          categoriesList: categoriesList.data,
          typesList: typesList.data,
          allergiesList: allergiesList.data,
          variantGroupsList: variantGroupsList.data || [],
        });
      } catch (error) {
        toast.error(error.message);
        console.error(error);
      }
    };
    fetchListData();
  }, [setDropDownOptions, getRestaurantDishCategoriesRequest, restaurantId]);

  const handleCreateDishCategory = useCallback(
    async (label) => {
      addRestaurantDishCategoryRequest({
        restaurantId,
        label,
        dishTypeId: formData.dishtype?.value
          ? formData.dishtype?.value?.dishTypeId
          : formData.dishtype?.dishTypeId,
      })
        .then((res) => {
          setDropDownOptions((prev) => ({
            ...prev,
            categoriesList: [...prev.categoriesList, res.data],
          }));
          onChange({
            ...formData,
            dishcategory: {
              label: res.data.label,
              value: res.data,
            },
          });
        })
        .catch((error) => {
          toast.error(error.message);
          console.error(error);
        });
    },
    [
      addRestaurantDishCategoryRequest,
      restaurantId,
      setDropDownOptions,
      onChange,
      formData,
    ]
  );

  const handleCategoriesManagementModal = useCallback(() => {
    openCategoryModal();
  }, [openCategoryModal]);

  const handleVariantsManagementModal = useCallback(() => {
    openVariantModal();
  }, [openVariantModal]);

  useEffect(() => {
    if (
      dropDownOptions.categoriesList &&
      dropDownOptions.typesList &&
      dropDownOptions.allergiesList &&
      dropDownOptions.variantGroupsList
    ) {
      setFields([
        {
          name: "imagePath",
          placeholder: t(`${tKey}imagePath.placeholder`),
          type: "file",
          accept: "image/*",
          isRemoveable: true,
          class: "w-100",
          dataTour: "dish-form-image",
          validate: (value) => {
            if (value?.size > 10000000)
              return t(`${tKey}errors.imagePath.maxSize`);
            return "";
          },
        },
        {
          name: "name",
          placeholder: t(`${tKey}name.placeholder`),
          type: "text",
          class: "w-100",
          dataTour: "dish-form-title-description",
          validate: (value) => {
            if (!value) return t(`${tKey}errors.name.required`);
            if (value.length < 2) return t(`${tKey}errors.name.min`);
            if (value.length > 25) return t(`${tKey}errors.name.max`);
            return "";
          },
        },
        {
          name: "description",
          placeholder: t(`${tKey}description.placeholder`),
          type: "textarea",
          class: "w-100",
          dataTour: "dish-form-title-description",
          rows: 4,
          validate: (value) => {
            if (value.length > 255) return t(`${tKey}errors.description.max`);
            return "";
          },
        },
        {
          name: "isVegetarian",
          label: t(`${tKey}isVegetarian.label`),
          type: "checkbox",
          class: "flex-grow bg-white p-3 rounded mr-1",
          dataTour: "dish-form-dietary",
          checkboxStyle: {
            coreStyle: {
              borderRadius: 50,
              borderWidth: 1,
              size: 20,
              borderColor: "#1AE170",
              style: {
                overflow: "hidden",
              },
            },
            iconStyle: {
              backgroundColor: "#1AE170",
              color: "white",
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
            },
          },
        },
        {
          name: "isVegan",
          label: t(`${tKey}isVegan.label`),
          type: "checkbox",
          class: "flex-grow bg-white p-3 rounded ml-1",
          dataTour: "dish-form-dietary",
          checkboxStyle: {
            coreStyle: {
              borderRadius: 50,
              borderWidth: 1,
              size: 20,
              borderColor: "#1AE170",
              style: {
                overflow: "hidden",
              },
            },
            iconStyle: {
              backgroundColor: "#1AE170",
              color: "white",
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
            },
          },
        },
        {
          name: "isHallal",
          label: t(`${tKey}isHallal.label`),
          type: "checkbox",
          class: "flex-grow bg-white p-3 rounded ml-1",
          dataTour: "dish-form-dietary",
          checkboxStyle: {
            coreStyle: {
              borderRadius: 50,
              borderWidth: 1,
              size: 20,
              borderColor: "#1AE170",
              style: {
                overflow: "hidden",
              },
            },
            iconStyle: {
              backgroundColor: "#1AE170",
              color: "white",
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              alignSelf: "stretch",
            },
          },
        },
        {
          name: "allergies",
          placeholder: t(`${tKey}allergies.placeholder`),
          type: "dropdown",
          multiple: true,
          class: "w-100",
          dataTour: "dish-form-dietary",
          options: dropDownOptions.allergiesList,
          optionFormat: (value) => {
            let imgSrc;
            try {
              imgSrc = require(`../../../../assets/img/allergies/${value.label}.svg`);
            } catch (e) {
              imgSrc = null;
            }
            return (
              <div className="flex align-items-center gap-2">
                {imgSrc && (
                  <img
                    height={20}
                    src={imgSrc}
                    alt={value.label}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <span>{t(`utils.allergies.${value.label}`)}</span>
              </div>
            );
          },
        },
        {
          name: "dishtype",
          placeholder: t(`${tKey}type.placeholder`),
          type: "dropdown",
          class: "col-6-responsive pr-1",
          dataTour: "dish-form-type",
          isLocked: !!lockedTypeId,
          options: dropDownOptions.typesList
            .filter((t) => t.standAlone)
            .map((option) => ({
              ...option,
              label: t(`utils.dishTypes.${option.label}`),
            })),
          validate: (value) => {
            if (!value) return t(`${tKey}errors.type.required`);
            return "";
          },
          optionFormat: (option) => {
            return option.label;
          },
        },
        {
          name: "dishcategory",
          placeholder: t(`${tKey}category.placeholder`),
          type: "dropdown",
          class: "col-6-responsive pl-1",
          dataTour: "dish-form-type",
          onAdd: handleCreateDishCategory,
          isClearable: true,
          info: t(`${tKey}category.info`),
          disabled: !formData.dishtype,
          options: dropDownOptions.categoriesList
            .filter(
              (c) => c.dishTypeId === formData.dishtype?.value?.dishTypeId
            )
            .map((option) => ({
              ...option,
              label: option.label,
            })),
          validate: (value) => {
            if (
              !value &&
              !!formData.dishtype &&
              (!!formData.dishtype?.value?.requireCategory ||
                !!formData.dishtype?.requireCategory)
            )
              return t(`${tKey}errors.category.required`);
            return "";
          },
          optionFormat: (option) => {
            return option.label;
          },
          rightElement: (
            <Button
              variant="info"
              size="sm"
              rounded
              onClick={() => handleCategoriesManagementModal()}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          ),
        },
        {
          name: "variantGroups",
          placeholder: t(`${tKey}variantGroups.placeholder`),
          type: "dropdown",
          multiple: true,
          class: "w-100",
          dataTour: "dish-form-variants",
          options: dropDownOptions.variantGroupsList.map((g) => ({
            ...g,
            label: g.label,
          })),
          optionFormat: (option) => option.label,
          rightElement: (
            <Button
              variant="info"
              size="sm"
              rounded
              onClick={() => handleVariantsManagementModal()}
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          ),
        },
        {
          name: "price",
          placeholder: t(`${tKey}price.placeholder`),
          type: "number",
          class: "col-4",
          inputContainerClassName: "input-chf",
          dataTour: "dish-form-price",
          validate: (value) => {
            if (!value) return t(`${tKey}errors.price.required`);
            return "";
          },
          onBlur: (event, updateFormState, field) => {
            const formattedValue = formatSwissPrice(event.target.value);
            updateFormState(field, formattedValue);
          },
        },
        {
          name: "discount",
          placeholder: t(`${tKey}discount.placeholder`),
          type: "number",
          class: "col-4 px-2",
          inputContainerClassName: "input-percent",
          dataTour: "dish-form-price",
          validate: (value) => {
            if (value >= 100) return t(`${tKey}errors.discount.max`);
            return "";
          },
        },
        {
          name: "finalPrice",
          placeholder: t(`${tKey}finalPrice.placeholder`),
          type: "number",
          class: "col-4",
          inputContainerClassName: "input-chf",
          dataTour: "dish-form-price",
          disabled: true,
          value: formData?.finalPrice,
          valueFormat: (value) => {
            return formatSwissPrice(value);
          },
        },
      ]);
    }
  }, [
    dropDownOptions,
    dropDownOptions.categoriesList,
    t,
    formData.finalPrice,
    formData.dishtype,
    formData.dishType?.dishTypeId,
    lockedTypeId,
    dishId,
    tKey,
    setFields,
    setDropDownOptions,
    handleCreateDishCategory,
    handleCategoriesManagementModal,
    handleVariantsManagementModal,
  ]);

  useEffect(() => {
    if (lockedTypeId && dropDownOptions.typesList && !hasUpdatedType.current) {
      const lockedType = dropDownOptions.typesList.find(
        (type) => type.dishTypeId === lockedTypeId
      );
      if (
        lockedType &&
        formData?.dishtype?.value?.dishTypeId !== lockedTypeId
      ) {
        onChange({
          ...formData,
          dishtype: {
            label: t(`utils.dishTypes.${lockedType.label}`),
            value: lockedType,
          },
        });
        hasUpdatedType.current = true;
      }
    }
  }, [lockedTypeId, dropDownOptions.typesList, formData, onChange, t]);

  useEffect(() => {
    const dishTypeId = formData.dishtype?.value
      ? formData.dishtype.value.dishTypeId
      : formData.dishtype?.dishTypeId;
    if (!dishTypeId) return;
    if (lastDishTypeRef.current !== dishTypeId) {
      onChange({
        ...formData,
        dishcategory: null,
      });
    }
    lastDishTypeRef.current = dishTypeId;
  }, [formData.dishtype, onChange, formData]);

  const handleChange = useCallback(
    (data) => {
      onChange({
        ...data,
        finalPrice: data.discount
          ? data.price - (data.price * data.discount) / 100
          : data.price,
      });
    },
    [onChange]
  );

  const handleUpdateCategories = useCallback(
    (categories) => {
      setDropDownOptions((prev) => ({
        ...prev,
        categoriesList: categories,
      }));
      // update the selected category label in the formData if it has changed
      const selectedId = formData.dishcategory?.value?.dishCategoryId;
      if (selectedId == null) return;
      const selected = categories.find((c) => c.dishCategoryId === selectedId);
      if (!selected)
        return onChange({
          ...formData,
          dishcategory: null,
        });
      onChange({
        ...formData,
        dishcategory: {
          value: selected,
          label: selected.label,
        },
      });
    },
    [onChange, formData]
  );

  const getImagePath = useCallback(() => {
    // Prioritize newly uploaded file (File object)
    if (formData?.imagePath instanceof File) {
      return formData.imagePath;
    }
    // Otherwise use fullImagePath from formData
    return formData?.fullImagePath || formData?.imagePath || null;
  }, [formData]);

  const getInitiales = useCallback(() => {
    // Use first letter of dish name if available
    const name = formData?.name || "";
    if (name) {
      return name.charAt(0).toUpperCase();
    }
    return ".";
  }, [formData]);

  return (
    <>
      <div className="flex gap-2 align-start">
        <div className="flex-1">
          <Form
            ref={ref}
            fields={fields}
            currentForm={formData}
            setFormDetails={handleChange}
            setIsValid={setIsValid}
            fieldClass={fieldClass}
          />
        </div>
        {imgPreview && getImagePath() && (
          <UserAvatar
            src={getImagePath()}
            initiales={getInitiales()}
            size="70"
          />
        )}
      </div>

      {/* Category management modal */}
      <Modal isOpen={isCategoryModalOpen} onClose={closeCategoryModal}>
        <CategoryManager
          tKey={tKey}
          types={dropDownOptions.typesList}
          initialCategories={dropDownOptions.categoriesList}
          updateCategories={handleUpdateCategories}
        />
      </Modal>

      {/* Variant group management modal */}
      <Modal
        id="modal-variant-groups"
        isOpen={isVariantModalOpen}
        onClose={closeVariantModal}
        noFooter
        noBgClickable
      >
        <VariantGroupManager
          dishId={dishId}
          initialAssignedGroupIds={(formData?.variantGroups || []).map(
            (g) => g.value?.dishVariantGroupId || g.dishVariantGroupId,
          )}
          tKey={tKey}
          onAssignmentChange={(groupIds) => {
            const allGroups = dropDownOptions.variantGroupsList || [];
            const newGroups = groupIds.map((id) => {
              const group = allGroups.find(
                (g) => g.dishVariantGroupId === id,
              );
              return group
                ? { label: group.label, value: group }
                : { label: "...", value: { dishVariantGroupId: id } };
            });
            onChange({ ...formData, variantGroups: newGroups });
          }}
          onGroupsChange={(updatedGroups) => {
            setDropDownOptions((prev) => ({
              ...prev,
              variantGroupsList: updatedGroups,
            }));
          }}
        />
        <div className="modal-footer">
          <Button variant="primary" onClick={closeVariantModal}>
            {t("utils.common.close")}
          </Button>
        </div>
      </Modal>
    </>
  );
});

export default DishForm;
